import React, { useState, useRef } from 'react';
import FileDropZone from '../shared/FileDropZone';
import '../CRMProfileScreen.css';
import { useFlashMessage } from '../FlashMessage/FlashMessageContext';
import DocumentNameSuggestionForm from '../DocumentNaming/DocumentNameSuggestionForm';

const KYCUploadModal = ({ 
    isOpen, 
    onClose, 
    kycDocumentTypes,
    onDocumentSelect,
    uploading,
    profileId,
    partnerName,
}) => {
    const [selectedKycType, setSelectedKycType] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [namingPayload, setNamingPayload] = useState(null);
    const fileInputRef = useRef(null);
    const { showError } = useFlashMessage();
    
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSelectedFile(null);
            return;
        }
        
        if (!selectedKycType) {
            showError('Please select a KYC document type first');
            event.target.value = ''; // Reset file input
            return;
        }
        
        setSelectedFile(file);
    };

    const acceptDroppedFile = (file) => {
        if (!selectedKycType) {
            showError('Please select a KYC document type first');
            return;
        }
        setSelectedFile(file);
    };
    
    const handleConfirm = () => {
        if (!selectedFile || !selectedKycType) {
            showError('Please select both a KYC document type and a file');
            return;
        }
        
        onDocumentSelect(selectedKycType, selectedFile, expiryDate || null, namingPayload);
        
        // Reset form
        setSelectedKycType('');
        setExpiryDate('');
        setSelectedFile(null);
        setNamingPayload(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        
        onClose();
    };
    
    const handleCancel = () => {
        // Reset all state
        setSelectedKycType('');
        setExpiryDate('');
        setSelectedFile(null);
        setNamingPayload(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
    
    if (!isOpen) return null;

    const uploadDisabled = !selectedFile || !selectedKycType || uploading || !namingPayload?.isValid;

    return (
        <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        }}>
            <div className="modal-content" style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '2rem',
                minWidth: '500px',
                maxWidth: '600px',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
            }}>
                <div className="modal-header" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    paddingBottom: '1rem',
                    borderBottom: '1px solid #e5e7eb'
                }}>
                    <h3 style={{ 
                        margin: 0, 
                        color: '#4a6b25', 
                        fontSize: '1.25rem', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        Upload KYC Document
                    </h3>
                    <button
                        onClick={handleCancel}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: '#6b7280',
                            padding: '0.25rem'
                        }}
                    >
                        ×
                    </button>
                </div>

                <div className="modal-body">
                    {/* KYC Document Type Selection */}
                    <div className="crm-input-group" style={{ marginBottom: '1.5rem' }}>
                        <label className="crm-input-label required">Select KYC Document Type</label>
                        <select 
                            value={selectedKycType}
                            onChange={(e) => setSelectedKycType(e.target.value)}
                            style={{ 
                                width: '100%', 
                                padding: '0.75rem 1rem', 
                                marginTop: '0.25rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                outline: 'none',
                                appearance: 'none',
                                backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6,9 12,15 18,9\'%3e%3c/polyline%3e%3c/svg%3e")',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 0.75rem center',
                                backgroundSize: '1rem',
                                paddingRight: '2.5rem',
                                color: selectedKycType ? '#374151' : '#9ca3af',
                                fontWeight: '500',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#4a6b25';
                                e.target.style.boxShadow = '0 0 0 3px rgba(74, 107, 37, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e5e7eb';
                                e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                            }}
                            disabled={uploading}
                        >
                            <option value="">-- Select KYC Document Type --</option>
                            {kycDocumentTypes.map(docType => (
                                <option key={docType} value={docType}>
                                    {docType}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Expiry Date */}
                    <div className="crm-input-group" style={{ marginBottom: '1.5rem' }}>
                        <label className="crm-input-label">Document Expiry Date (Optional)</label>
                        <input
                            type="date"
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem 1rem',
                                marginTop: '0.25rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                outline: 'none',
                                color: '#374151',
                                fontWeight: '500',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#4a6b25';
                                e.target.style.boxShadow = '0 0 0 3px rgba(74, 107, 37, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e5e7eb';
                                e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                            }}
                            disabled={uploading}
                        />
                        <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#6b7280', 
                            marginTop: '0.5rem' 
                        }}>
                            Leave blank if document does not expire
                        </div>
                    </div>
                    
                    {/* File Upload Section */}
                    <div className="crm-input-group" style={{ marginBottom: '1.5rem' }}>
                        <label className="crm-input-label required">Select File</label>
                        
                        <FileDropZone
                            className="file-drop-zone"
                            disabled={!selectedKycType || uploading}
                            onFiles={(files) => acceptDroppedFile(files[0])}
                            onError={showError}
                            style={{
                                border: '2px dashed #d1d5db',
                                borderRadius: '8px',
                                padding: '2rem',
                                textAlign: 'center',
                                backgroundColor: '#f9fafb',
                                transition: 'all 0.2s ease',
                                cursor: selectedKycType && !uploading ? 'pointer' : 'not-allowed',
                                opacity: selectedKycType ? 1 : 0.6
                            }}
                            onClick={() => selectedKycType && !uploading && fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={handleFileSelect}
                                accept=".pdf,.docx,.xlsx,.pptx,.md,.html,.csv,.png,.jpg,.jpeg,.tiff,.bmp,.webp,.txt,.eml,.msg,.adoc"
                                style={{ display: 'none' }}
                                disabled={!selectedKycType || uploading}
                            />
                            
                            {selectedFile ? (
                                <div style={{ color: '#4a6b25' }}>
                                    <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                                        {selectedFile.name}
                                    </div>
                                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                        {formatFileSize(selectedFile.size)}
                                    </div>
                                </div>
                            ) : (
                                <div style={{ color: '#6b7280' }}>
                                    <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                                        {selectedKycType ? 'Drop files here or click to browse' : 'Please select a KYC document type first'}
                                    </div>
                                    <div style={{ fontSize: '0.875rem' }}>
                                        Supports: PDF, DOCX, XLSX, PPTX, Markdown (.md), HTML, CSV, Images (PNG, JPG, JPEG, TIFF, BMP, WEBP), TXT, EML, MSG, AsciiDoc (Max 30MB)
                                    </div>
                                </div>
                            )}
                        </FileDropZone>
                    </div>

                    {/* AI Document Name Suggestion */}
                    {selectedFile && selectedKycType && (
                        <div style={{ marginBottom: '1.5rem' }}>
                            <DocumentNameSuggestionForm
                                autoRun
                                file={selectedFile}
                                module="kyc"
                                relatedEntityId={profileId || ''}
                                partnerName={partnerName || ''}
                                kycSubtype={selectedKycType}
                                onChange={setNamingPayload}
                                disabled={uploading}
                            />
                        </div>
                    )}

                    {/* File Information */}
                    {selectedFile && (
                        <div style={{
                            padding: '1rem',
                            backgroundColor: '#f0f9ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '8px',
                            marginBottom: '1.5rem'
                        }}>
                            <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                                <strong>File Details:</strong>
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#374151', marginTop: '0.5rem' }}>
                                <div><strong>Name:</strong> {selectedFile.name}</div>
                                <div><strong>Size:</strong> {formatFileSize(selectedFile.size)}</div>
                                <div><strong>Type:</strong> {selectedFile.type || 'Unknown'}</div>
                                <div><strong>KYC Document Type:</strong> {selectedKycType}</div>
                                {expiryDate && <div><strong>Expiry Date:</strong> {expiryDate}</div>}
                                <div><strong>Last Modified:</strong> {new Date(selectedFile.lastModified).toLocaleDateString()}</div>
                            </div>
                        </div>
                    )}

                    {/* Upload Guidelines */}
                    <div style={{
                        padding: '1rem',
                        backgroundColor: '#fef3c7',
                        border: '1px solid #f59e0b',
                        borderRadius: '8px',
                        marginBottom: '1.5rem'
                    }}>
                        <div style={{ fontSize: '0.875rem', color: '#92400e' }}>
                            <strong>KYC Document Guidelines:</strong>
                        </div>
                        <ul style={{ fontSize: '0.875rem', color: '#92400e', marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
                            <li>Upload official KYC documents such as passports, ID cards, certificates</li>
                            <li>Select appropriate document type for proper classification</li>
                            <li>Set expiry date if applicable for compliance tracking</li>
                            <li>Maximum file size: 30MB</li>
                            <li>Supported formats: PDF, DOCX, XLSX, PPTX, MD, HTML, CSV, TXT, JPG, PNG, and more</li>
                            <li>Documents will be securely stored for compliance purposes</li>
                        </ul>
                    </div>
                </div>

                <div className="modal-footer" style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #e5e7eb'
                }}>
                    <button
                        onClick={handleCancel}
                        disabled={uploading}
                        style={{
                            background: 'transparent',
                            border: '1px solid #d1d5db',
                            color: '#374151',
                            borderRadius: '8px',
                            padding: '0.75rem 1.5rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            opacity: uploading ? 0.6 : 1,
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={uploadDisabled}
                        title={
                            selectedFile && selectedKycType && !uploading && !namingPayload?.isValid
                                ? 'Add a document name before uploading'
                                : undefined
                        }
                        style={{
                            background: uploadDisabled
                                ? '#6c757d'
                                : 'linear-gradient(135deg, #f59e0b, #d97706)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.75rem 1.5rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: uploadDisabled ? 'not-allowed' : 'pointer',
                            opacity: uploadDisabled ? 0.6 : 1,
                            transition: 'all 0.2s',
                            boxShadow: uploadDisabled
                                ? 'none'
                                : '0 2px 4px rgba(245, 158, 11, 0.25)'
                        }}
                    >
                        {uploading ? 'Uploading...' : 'Upload Document'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default KYCUploadModal; 